import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { apiService } from '../../../services/api';
import { queryKeys } from '../../../hooks/useApi';
import {
  getSmartSortingFileError,
  resolveSmartSortingMimeType,
} from '../lib/file';

// Finalize each file before starting the next one. Users can still select a
// batch, while the network flow follows the reliable one-by-one path.
const MAX_CONCURRENT = 1;

export type UploadFileStatus =
  | 'pending'
  | 'uploading'
  | 'finalizing'
  | 'done'
  | 'error';

export interface UploadFileEntry {
  id: string;
  file: File;
  status: UploadFileStatus;
  progress: number;
  error: string | null;
  taskId: string | null;
}

async function computeSha256(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

function getUploadErrorMessage(error: unknown): string {
  if (!(error instanceof Error)) return 'Error desconocido';
  const detail = 'detail' in error ? error.detail : null;
  return typeof detail === 'string' && detail ? detail : error.message;
}

export function useSmartSortingUploads() {
  const [queue, setQueue] = useState<UploadFileEntry[]>([]);
  const activeCount = useRef(0);
  const queueRef = useRef<UploadFileEntry[]>([]);
  const queryClient = useQueryClient();

  const updateEntry = useCallback(
    (id: string, patch: Partial<UploadFileEntry>) => {
      const next = queueRef.current.map((entry) =>
        entry.id === id ? { ...entry, ...patch } : entry,
      );
      queueRef.current = next;
      setQueue(next);
    },
    [],
  );

  const processNext = useCallback(async () => {
    if (activeCount.current >= MAX_CONCURRENT) return;

    const pendingIndex = queueRef.current.findIndex(
      (entry) => entry.status === 'pending',
    );
    if (pendingIndex < 0) return;

    const pending = queueRef.current[pendingIndex];
    if (!pending) return;

    const claimedEntry: UploadFileEntry = {
      ...pending,
      status: 'uploading',
      progress: 0,
    };
    const claimedQueue = queueRef.current.map((entry, index) =>
      index === pendingIndex ? claimedEntry : entry,
    );
    queueRef.current = claimedQueue;
    setQueue(claimedQueue);
    activeCount.current += 1;
    const entryId = claimedEntry.id;
    const file = claimedEntry.file;

    try {
      const initResponse = await apiService.initSmartSortingUpload({
        original_filename: file.name,
        mime_type: resolveSmartSortingMimeType(file),
        size_bytes: file.size,
      });

      await axios.put(initResponse.presigned_put_url, file, {
        headers: initResponse.presigned_put_headers,
        onUploadProgress: (progressEvent) => {
          const pct = progressEvent.total
            ? Math.round((progressEvent.loaded / progressEvent.total) * 100)
            : 0;
          updateEntry(entryId, { progress: pct });
        },
      });

      updateEntry(entryId, { status: 'finalizing', progress: 100 });

      const checksum = await computeSha256(file);

      const finalizeResponse = await apiService.finalizeSmartSortingFile({
        file_id: initResponse.file_id,
        storage_key: initResponse.storage_key,
        original_filename: file.name,
        checksum,
      });

      updateEntry(entryId, {
        status: 'done',
        taskId: finalizeResponse.task_id,
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.smartSorting.summary,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.userFiles.all,
      });
    } catch (err) {
      updateEntry(entryId, {
        status: 'error',
        error: getUploadErrorMessage(err),
      });
    } finally {
      activeCount.current -= 1;
      processNext();
    }
  }, [updateEntry, queryClient]);

  const addFiles = useCallback(
    (files: File[]) => {
      const entries: UploadFileEntry[] = [];
      const errors: string[] = [];

      for (const file of files) {
        const validationError = getSmartSortingFileError(file);
        if (validationError) {
          errors.push(`${file.name}: ${validationError}`);
          continue;
        }
        entries.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file,
          status: 'pending',
          progress: 0,
          error: null,
          taskId: null,
        });
      }

      if (entries.length > 0) {
        const next = [...queueRef.current, ...entries];
        queueRef.current = next;
        setQueue(next);

        setTimeout(() => {
          for (let i = 0; i < MAX_CONCURRENT; i++) {
            processNext();
          }
        }, 0);
      }

      return errors;
    },
    [processNext],
  );

  const clearCompleted = useCallback(() => {
    const next = queueRef.current.filter(
      (entry) => entry.status !== 'done' && entry.status !== 'error',
    );
    queueRef.current = next;
    setQueue(next);
  }, []);

  const isUploading = queue.some(
    (e) =>
      e.status === 'pending' ||
      e.status === 'uploading' ||
      e.status === 'finalizing',
  );

  return {
    queue,
    addFiles,
    clearCompleted,
    isUploading,
  };
}
